import React, { useState } from 'react';
import { useZxing } from 'react-zxing';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Camera, X, Check } from 'lucide-react';

/**
 * BarcodeScanner Component
 * ========================
 * Camera-based barcode scanner using ZXing library
 * Supports EAN-13, UPC-A, Code 128, and other common formats
 */
const BarcodeScanner = ({ open, onClose, onScan }) => {
  const { t } = useTranslation();
  const [scanning, setScanning] = useState(true);
  const [scannedCode, setScannedCode] = useState('');
  const [error, setError] = useState('');
  const [cameraReady, setCameraReady] = useState(false);

  // Reset state when dialog opens, delay camera start for dialog animation
  React.useEffect(() => {
    if (open) {
      setScanning(true);
      setScannedCode('');
      setError('');
      setCameraReady(false);
      const timer = setTimeout(() => setCameraReady(true), 600);
      return () => clearTimeout(timer);
    } else {
      setCameraReady(false);
    }
  }, [open]);

  const { ref } = useZxing({
    onDecodeResult(result) {
      if (result && scanning) {
        const barcode = result.getText();
        setScannedCode(barcode);
        setScanning(false);
        
        // Vibrate if supported
        if (navigator.vibrate) {
          navigator.vibrate(200);
        }
        
        // Call parent handler
        if (onScan) {
          onScan(barcode);
        }
      }
    },
    onError(err) {
      console.error('Scanner error:', err);
      // Only show error if it's a real access denial, not a decode miss
      if (err?.name === 'NotAllowedError' || err?.name === 'NotFoundError' || err?.name === 'NotReadableError') {
        setError(t('diet.scannerError', 'Camera access denied or not available'));
        setScanning(false);
      }
    },
    constraints: {
      audio: false,
      video: { facingMode: 'environment' },
    },
    paused: !scanning || !open || !cameraReady,
  });

  const handleRescan = () => {
    setScannedCode('');
    setError('');
    setScanning(true);
  };

  const handleClose = () => {
    setScanning(false);
    setScannedCode('');
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]" data-testid="barcode-scanner-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5" />
            {t('diet.scanBarcode', 'Scan Barcode')}
          </DialogTitle>
          <DialogDescription>
            {t('diet.scanBarcodeDescription', 'Position the barcode within the camera frame')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Camera View */}
          <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
            {scanning && cameraReady && (
              <video
                ref={ref}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
                style={{ minHeight: '200px' }}
                data-testid="barcode-scanner-video"
              />
            )}
            
            {scanning && !cameraReady && (
              <div className="absolute inset-0 flex items-center justify-center bg-black">
                <div className="text-center text-white">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                  <p className="text-sm">{t('diet.startingCamera', 'Starting camera...')}</p>
                </div>
              </div>
            )}

            {scanning && cameraReady && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-64 h-40 border-2 border-primary rounded-lg shadow-lg">
                  <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-primary" />
                  <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-primary" />
                  <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-primary" />
                  <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-primary" />
                </div>
              </div>
            )}

            {!scanning && scannedCode && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/80">
                <div className="text-center text-white">
                  <Check className="h-16 w-16 mx-auto mb-4 text-green-500" />
                  <p className="text-lg font-semibold">{t('diet.barcodeScanned', 'Barcode Scanned!')}</p>
                  <p className="text-sm text-gray-300 mt-2">{scannedCode}</p>
                </div>
              </div>
            )}

            {!scanning && !scannedCode && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                <Loader2 className="h-12 w-12 animate-spin text-white" />
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <Alert variant="destructive" data-testid="scanner-error-alert">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Scanned Code Display */}
          {scannedCode && (
            <Alert className="bg-green-50 border-green-200" data-testid="scanned-code-display">
              <Check className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                {t('diet.scannedCode', 'Scanned')}: <strong>{scannedCode}</strong>
              </AlertDescription>
            </Alert>
          )}

          {/* Instructions */}
          {scanning && !error && (
            <div className="text-sm text-muted-foreground text-center">
              <p>{t('diet.scannerInstructions', 'Hold steady and ensure good lighting for best results')}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2 justify-end">
            <Button
              variant="outline"
              onClick={handleClose}
              data-testid="scanner-cancel-button"
            >
              <X className="h-4 w-4 mr-2" />
              {t('common.cancel', 'Cancel')}
            </Button>
            
            {scannedCode && (
              <Button
                variant="outline"
                onClick={handleRescan}
                data-testid="scanner-rescan-button"
              >
                <Camera className="h-4 w-4 mr-2" />
                {t('diet.scanAgain', 'Scan Again')}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BarcodeScanner;
